import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Settings,
  Package,
  MessageSquare,
  CreditCard,
  Users,
  HelpCircle,
  Activity,
  Search,
  Menu,
  X,
  ChevronDown,
  LogOut,
  User,
  ShoppingBag,
  BookOpen,
  Headphones,
  BarChart2,
  Lock,
} from 'lucide-react';
import { authService } from '../../services/authService';
import LanguageSwitcher from '../LanguageSwitcher';
import NotificationBell from '../notifications/NotificationBell';
import { useCmmsAuth } from '../../contexts/CmmsAuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import { useCompany } from '../../contexts/CompanyContext';

const PortalLayout = () => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [headerUserMenuOpen, setHeaderUserMenuOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const sidebarUserMenuRef = useRef(null);
  const headerUserMenuRef = useRef(null);

  // Get CMMS auth context for permission checks
  const { hasPermission, cmmsUser, loading: cmmsLoading } = useCmmsAuth();

  // Get company data and current user profile for sidebar/header
  const { company: companyData, currentUserProfile } = useCompany();

  useEffect(() => {
    // Get user data from localStorage
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Get subscription flags and role — kept in state so the overlay reacts when billing refreshes them
  const [hasCmmsSubscription, setHasCmmsSubscription] = useState(localStorage.getItem('hasCmmsSubscription') === 'true');
  const [hasChatbotSubscription, setHasChatbotSubscription] = useState(localStorage.getItem('hasChatbotSubscription') === 'true');
  const [cmmsSubscriptionStatus, setCmmsSubscriptionStatus] = useState(localStorage.getItem('cmmsSubscriptionStatus'));
  const [chatbotSubscriptionStatus, setChatbotSubscriptionStatus] = useState(localStorage.getItem('chatbotSubscriptionStatus'));
  const companyRole = localStorage.getItem('companyRole');

  // Re-read subscription status from localStorage when Billing page updates it after renewal
  useEffect(() => {
    const refresh = () => {
      setHasCmmsSubscription(localStorage.getItem('hasCmmsSubscription') === 'true');
      setHasChatbotSubscription(localStorage.getItem('hasChatbotSubscription') === 'true');
      setCmmsSubscriptionStatus(localStorage.getItem('cmmsSubscriptionStatus'));
      setChatbotSubscriptionStatus(localStorage.getItem('chatbotSubscriptionStatus'));
    };
    window.addEventListener('subscriptionStatusUpdated', refresh);
    return () => window.removeEventListener('subscriptionStatusUpdated', refresh);
  }, []);
  const isCompanyOwner = companyRole === 'COMPANY_OWNER';

  const isExpiredStatus = (status) => status === 'EXPIRED' || status === 'SUSPENDED';
  const showCmmsExpiredOverlay = location.pathname.startsWith('/portal/cmms') && isExpiredStatus(cmmsSubscriptionStatus);
  const showChatbotExpiredOverlay = location.pathname.startsWith('/portal/chatbot') && isExpiredStatus(chatbotSubscriptionStatus);
  const showExpiredOverlay = showCmmsExpiredOverlay || showChatbotExpiredOverlay;

  // chatbotHasAgents: true unless the plan explicitly sets includedAgents to 0
  const chatbotHasAgents = localStorage.getItem('chatbotIncludedAgents') !== '0';

  const isChatAgent = companyRole === 'CHAT_AGENT';
  const isChatSupervisor = companyRole === 'CHAT_SUPERVISOR';

  // Close sidebar user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarUserMenuRef.current && !sidebarUserMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  // Close header user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerUserMenuRef.current && !headerUserMenuRef.current.contains(event.target)) {
        setHeaderUserMenuOpen(false);
      }
    };

    if (headerUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [headerUserMenuOpen]);

  const toggleMenu = (menuName) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    setHeaderUserMenuOpen(false);
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    authService.logout();
  };

  // Define all navigation items with subscription and permission requirements.
  // `name` is the i18n key path — used as React key, toggle-state key, and rendered via t(item.name).
  const allNavigation = [
    {
      name: 'nav.dashboard',
      href: '/portal',
      icon: LayoutDashboard,
      requiresSubscription: false,
      requiresRole: 'COMPANY_OWNER',
    },
    {
      name: 'nav.cmms',
      icon: Package,
      requiresSubscription: 'cmms',
      children: [
        { name: 'cmms.dashboard',     href: '/portal/cmms',                  permission: PERMISSIONS.VIEW_DASHBOARD },
        { name: 'cmms.team',          href: '/portal/cmms/team',              permission: PERMISSIONS.VIEW_TEAM },
        { name: 'cmms.branches',      href: '/portal/cmms/branches',          permission: PERMISSIONS.VIEW_BRANCHES },
        { name: 'cmms.locations',     href: '/portal/cmms/locations',         permission: PERMISSIONS.VIEW_LOCATIONS },
        { name: 'cmms.assets',        href: '/portal/cmms/assets',            permission: PERMISSIONS.VIEW_ASSETS },
        { name: 'cmms.workOrders',    href: '/portal/cmms/work-orders',       permission: PERMISSIONS.VIEW_WORK_ORDERS },
        { name: 'cmms.suppliers',     href: '/portal/cmms/suppliers',         permission: PERMISSIONS.VIEW_SUPPLIERS },
        { name: 'cmms.contracts',     href: '/portal/cmms/contracts',         permission: PERMISSIONS.VIEW_CONTRACTS },
        { name: 'cmms.pmSchedules',   href: '/portal/cmms/pm-schedules',      permission: PERMISSIONS.VIEW_PM_SCHEDULES },
        { name: 'cmms.visits',        href: '/portal/cmms/visits',            permission: PERMISSIONS.VIEW_CONTRACTS },
        { name: 'cmms.serviceReports',href: '/portal/cmms/service-reports',   permission: PERMISSIONS.VIEW_CONTRACTS },
        { name: 'cmms.warehouses',    href: '/portal/cmms/warehouses',        permission: PERMISSIONS.VIEW_BRANCHES },
        { name: 'cmms.inventory',     href: '/portal/cmms/inventory',         permission: PERMISSIONS.VIEW_ASSETS },
        { name: 'cmms.reports',       href: '/portal/cmms/reports',           permission: PERMISSIONS.VIEW_REPORTS },
      ],
    },
    {
      name: 'nav.chatbot',
      icon: MessageSquare,
      requiresSubscription: 'chatbot',
      requiresRole: 'COMPANY_OWNER',
      children: [
        { name: 'chatbot.dashboard',     href: '/portal/chatbot' },
        { name: 'chatbot.manage',        href: '/portal/chatbot/manage' },
        { name: 'chatbot.conversations', href: '/portal/chatbot/conversations' },
        { name: 'chatbot.analytics',     href: '/portal/chatbot/analytics' },
        { name: 'chatbot.agents',        href: '/portal/chatbot/agents',          requiresAgents: true },
        { name: 'chatbot.liveInbox',     href: '/portal/chatbot/inbox',           requiresAgents: true },
        { name: 'agentAnalytics.navLabel', href: '/portal/chatbot/agent-analytics', requiresAgents: true },
      ],
    },
    {
      name: 'nav.account',
      icon: User,
      requiresSubscription: false,
      children: [
        { name: 'account.settings',      href: '/portal/account/settings' },
        { name: 'account.notifications', href: '/portal/account/notifications' },
      ],
    },
    {
      name: 'nav.products',
      href: '/portal/products',
      icon: ShoppingBag,
      requiresSubscription: false,
      requiresRole: 'COMPANY_OWNER',
    },
    {
      name: 'nav.billing',
      href: '/portal/billing',
      icon: CreditCard,
      requiresSubscription: false,
      requiresRole: 'COMPANY_OWNER',
    },
    {
      name: 'nav.docs',
      icon: BookOpen,
      requiresSubscription: false,
      children: [
        { name: 'nav.chatbotGuide', href: '/portal/help/chatbot' },
        { name: 'nav.cmmsGuide',    href: '/portal/help/cmms' },
      ],
    },
    {
      name: 'nav.support',
      href: '/portal/support',
      icon: HelpCircle,
      requiresSubscription: false
    },
    {
      name: 'nav.activityLog',
      href: '/portal/activity',
      icon: Activity,
      requiresSubscription: false,
      requiresRole: 'COMPANY_OWNER',
    },
  ];

  // Filter navigation based on subscriptions, roles, and permissions
  const getFilteredNavigation = useMemo(() => {
    // CHAT_SUPERVISOR: full chatbot management access, no company/CMMS sections
    if (isChatSupervisor && hasChatbotSubscription) {
      return [{
        name: 'nav.chatbot',
        icon: MessageSquare,
        children: [
          { name: 'chatbot.dashboard',     href: '/portal/chatbot' },
          { name: 'chatbot.manage',        href: '/portal/chatbot/manage' },
          { name: 'chatbot.conversations', href: '/portal/chatbot/conversations' },
          { name: 'chatbot.analytics',     href: '/portal/chatbot/analytics' },
          ...(chatbotHasAgents ? [
            { name: 'chatbot.agents',          href: '/portal/chatbot/agents' },
            { name: 'chatbot.liveInbox',       href: '/portal/chatbot/inbox' },
            { name: 'agentAnalytics.navLabel', href: '/portal/chatbot/agent-analytics' },
          ] : []),
        ],
      }];
    }
    if (isChatSupervisor) return [];

    // CHAT_AGENT: conversations + live inbox only
    if (isChatAgent && hasChatbotSubscription) {
      return [{
        name: 'nav.chatbot',
        icon: MessageSquare,
        children: [
          { name: 'chatbot.conversations',   href: '/portal/chatbot/conversations' },
          { name: 'chatbot.liveInbox',       href: '/portal/chatbot/inbox' },
          { name: 'agentAnalytics.navLabel', href: '/portal/chatbot/agent-analytics' },
        ],
      }];
    }
    if (isChatAgent) return [];

    return allNavigation
      .filter(item => {
        // Check role requirement
        if (item.requiresRole === 'COMPANY_OWNER' && !isCompanyOwner) {
          return false;
        }

        // Check subscription requirement
        if (item.requiresSubscription === 'cmms' && !hasCmmsSubscription) {
          return false;
        }
        if (item.requiresSubscription === 'chatbot' && !hasChatbotSubscription) {
          return false;
        }

        // Check permission requirement (skip if CMMS data is still loading)
        if (item.permission && !cmmsLoading && cmmsUser) {
          if (!hasPermission(item.permission)) {
            return false;
          }
        }

        return true;
      })
      .map(item => {
        // Filter children by permission
        if (item.children) {
          const filteredChildren = item.children.filter(child => {
            // Hide agent-only items when the plan has no included agents
            if (child.requiresAgents && !chatbotHasAgents) {
              return false;
            }
            // If child has no permission requirement, include it
            if (!child.permission) {
              return true;
            }
            // Skip permission check if still loading or no user
            if (cmmsLoading || !cmmsUser) {
              return true;
            }
            return hasPermission(child.permission);
          });

          // If no children left after filtering, exclude the parent
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...item,
            children: filteredChildren
          };
        }
        return item;
      })
      .filter(Boolean); // Remove null items
  }, [hasCmmsSubscription, hasChatbotSubscription, isCompanyOwner, isChatAgent, isChatSupervisor, chatbotHasAgents, cmmsLoading, cmmsUser, hasPermission]);

  const navigation = getFilteredNavigation;

  // Initialize open menus based on current route
  useEffect(() => {
    const newOpenMenus = {};
    navigation.forEach(item => {
      if (item.children) {
        const isChildActive = item.children.some(child =>
          location.pathname === child.href
        );
        if (isChildActive) {
          newOpenMenus[item.name] = true;
        }
      }
    });
    setOpenMenus(newOpenMenus);
  }, [location.pathname, navigation]);

  const isActive = (href) => location.pathname === href;
  const isParentActive = (children) => children?.some(child => location.pathname === child.href);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-gray-200 transition-all duration-300 ease-in-out flex flex-col flex-shrink-0`}
      >
        {/* Logo / Company Branding */}
        <div className="h-16 flex items-center justify-center px-4 border-b border-gray-200 flex-shrink-0">
          <Link to="/portal" className="flex items-center gap-2 min-w-0">
            {/* Company logo or fallback gradient square */}
            <div className="w-8 h-8 rounded-lg flex-shrink-0 overflow-hidden border border-gray-100">
              {companyData?.logoUrl ? (
                <img
                  src={companyData.logoUrl}
                  alt={companyData.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-primary-600 to-blue-600" />
              )}
            </div>
            {sidebarOpen && (
              <span className="font-bold text-base text-gray-900 truncate">
                {companyData?.name || 'Portal'}
              </span>
            )}
          </Link>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <div key={item.name}>
              {item.children ? (
                <div>
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className={`w-full flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'} px-3 py-2 text-sm font-medium rounded-lg cursor-pointer transition-all ${
                      isParentActive(item.children)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    title={!sidebarOpen ? t(item.name) : ''}
                  >
                    <div className={`flex items-center ${sidebarOpen ? 'gap-3' : ''}`}>
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {sidebarOpen && <span>{t(item.name)}</span>}
                    </div>
                    {sidebarOpen && <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${openMenus[item.name] ? 'rotate-180' : ''}`} />}
                  </button>
                  
                  {sidebarOpen && openMenus[item.name] && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`block px-3 py-2 text-sm rounded-lg ${
                            isActive(child.href)
                              ? 'bg-primary-50 text-primary-600 font-medium'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {t(child.name)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.href}
                  className={`flex items-center ${sidebarOpen ? 'gap-3' : 'justify-center'} px-3 py-2 text-sm font-medium rounded-lg ${
                    isActive(item.href)
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title={!sidebarOpen ? t(item.name) : ''}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span>{t(item.name)}</span>}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* User Section - Fixed at Bottom */}
        <div className="border-t border-gray-200 p-3 flex-shrink-0">
          <div className="relative" ref={sidebarUserMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={`w-full flex items-center ${sidebarOpen ? 'gap-3' : 'justify-center'} px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors`}
              title={!sidebarOpen ? `${user?.firstName} ${user?.lastName}` : ''}
            >
              <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center bg-gradient-to-r from-primary-600 to-blue-600">
                {currentUserProfile?.profilePhotoUrl
                  ? <img src={currentUserProfile.profilePhotoUrl} alt="" className="w-full h-full object-cover" />
                  : <span className="text-white font-bold text-sm">{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
                }
              </div>
              {sidebarOpen && (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
                    userMenuOpen ? 'rotate-180' : ''
                  }`} />
                </>
              )}
            </button>

            {/* Logout Popup */}
            {userMenuOpen && sidebarOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-red-600 hover:text-red-700"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('nav.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm flex-shrink-0">
          {/* Left side */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {sidebarOpen ? (
                <X className="w-5 h-5 text-gray-600" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>

          {/* Right side - User dropdown */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />

            <NotificationBell />

            {/* Header User Menu */}
            <div className="relative" ref={headerUserMenuRef}>
              <button
                onClick={() => setHeaderUserMenuOpen(!headerUserMenuOpen)}
                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-r from-primary-600 to-blue-600 flex-shrink-0">
                  {currentUserProfile?.profilePhotoUrl
                    ? <img src={currentUserProfile.profilePhotoUrl} alt="" className="w-full h-full object-cover" />
                    : <span className="text-white font-bold text-sm">{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
                  }
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{user?.companyRole}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                  headerUserMenuOpen ? 'rotate-180' : ''
                }`} />
              </button>

              {/* Dropdown Menu */}
              {headerUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                  </div>
                  <Link
                    to="/portal/account/settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setHeaderUserMenuOpen(false)}
                  >
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      {t('account.settings')}
                    </div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6 relative">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>

          {/* Expired subscription overlay — covers content but leaves sidebar accessible */}
          {showExpiredOverlay && (
            <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center z-40">
              <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {t('billing.subscriptionExpired')}
                </h2>
                <p className="text-gray-500 mb-6">{t('billing.renewToResume')}</p>
                <Link
                  to="/portal/billing"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  {t('billing.renewNow')}
                </Link>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <LogOut className="w-6 h-6 text-red-600" />
              </div>
              <button
                onClick={() => setShowLogoutModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('nav.logout')}</h2>
            <p className="text-sm text-gray-500 mb-6">{t('nav.logoutConfirm')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalLayout;
